import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Login.css';

function Login({ setIsAuthenticated, setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const convaiRef = useRef(null);

  // Add effect to load the ElevenLabs script
  useEffect(() => {
    let convaiElement = null;

    // Check if script is already loaded (global flag approach)
    if (!window.elevenLabsConvaiLoaded) {
      // Check if script element already exists
      const existingScript = document.querySelector('script[src="https://elevenlabs.io/convai-widget/index.js"]');
      
      if (!existingScript) {
        // Create and add script only if it doesn't exist
        const script = document.createElement('script');
        script.src = 'https://elevenlabs.io/convai-widget/index.js';
        script.async = true;
        script.type = 'text/javascript';
        
        // Set global flag to prevent duplicate loading
        window.elevenLabsConvaiLoaded = true;
        
        script.onload = () => {
          createConvaiElement();
        };
        
        script.onerror = () => {
          console.error('Failed to load ElevenLabs ConvAI script');
          window.elevenLabsConvaiLoaded = false;
        };
        
        document.body.appendChild(script);
      } else {
        // Script exists, just create element
        createConvaiElement();
      }
    } else {
      // Script already loaded, create element immediately
      createConvaiElement();
    }

    function createConvaiElement() {
      if (convaiRef.current && !convaiRef.current.querySelector('elevenlabs-convai')) {
        // Wait a bit for custom element to be defined
        const checkAndCreate = () => {
          if (customElements.get('elevenlabs-convai')) {
            convaiElement = document.createElement('elevenlabs-convai');
            convaiElement.setAttribute('agent-id', 'crQjNqa0zibqKmKu8d68');
            convaiRef.current.appendChild(convaiElement);
          } else {
            // Retry after a short delay
            setTimeout(checkAndCreate, 100);
          }
        };
        checkAndCreate();
      }
    }
    
    // Cleanup on unmount
    return () => {
      // Only clean up the convai element
      if (convaiElement && convaiRef.current?.contains(convaiElement)) {
        convaiRef.current.removeChild(convaiElement);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    // Existing submit handler code...
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      setUser(data.user);
      setIsAuthenticated(true);
    } catch (err) {
      if (err.message.includes('archived')) {
        setError('This account has been deactivated. Contact support for assistance.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Login</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          {/* Existing form fields... */}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className="forgot-password">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
          </div>
          <button type="submit" className="auth-button" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <p className="auth-link">
          Don't have an account? <Link to="/register">Register</Link>
        </p>
        
        {/* Add container for ElevenLabs Convai Widget */}
        <div ref={convaiRef} className="elevenlabs-widget-container"></div>
      </div>
    </div>
  );
}

export default Login;